<script setup lang="ts">
import { computed, Fragment, useSlots } from 'vue';
import type { VNode } from 'vue';
import generateRandomKey from '@/utilities/generateRandomKey';

type TypeRowData = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

defineProps<{
  data: TypeRowData[];
}>();

const slots = useSlots();

const childrenVNodes = computed(() => {
  // pull raw slot children (VNodeChild[])
  const nodes = slots.default?.() || [];
  // flatten fragments and extract only VNode objects
  const flattened = nodes.flatMap((node) => {
    if (node.type === Fragment && Array.isArray(node.children)) {
      return node.children;
    }
    return node;
  });
  // filter to VNode, excluding strings, numbers, booleans, arrays
  return flattened.filter((node): node is VNode => typeof node === 'object' && node !== null && 'type' in node);
});

const columns = computed(() =>
  childrenVNodes.value.filter(
    (node): node is VNode =>
      // ensure node.type is a component with a name
      (typeof node.type === 'object' || typeof node.type === 'function') &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((node.type as any).name === 'DataTableColumn' || (node.type as any).__name === 'DataTableColumn')
  )
);

function styleObjectToString(style: Record<string, string>): string {
  return Object.entries(style)
    .map(([key, value]) => key.replace(/([A-Z])/g, '-$1').toLowerCase() + ': ' + value)
    .join('; ');
}

function getHeaderStyle(col) {
  if (col.props['header-style']) {
    return (
      (typeof col.props['header-style'] === 'object'
        ? styleObjectToString(col.props['header-style'])
        : col.props['header-style']) +
      ' ' +
      getStyle(col)
    );
  }
  return '';
}

function getClass(col) {
  const classString = col.props['class'];
  if (classString) {
    return classString;
  }
  return '';
}

function getStyle(col) {
  if (col.props['style']) {
    return typeof col.props['style'] === 'object' ? styleObjectToString(col.props['style']) : col.props['style'];
  }
  return '';
}

function getHeaderClass(col) {
  const headerClass = (col.props['header-class'] || '') + ' ' + getClass(col);
  return headerClass.trim() || '';
}

function getBodyClass(col) {
  const bodyClass = (col.props['body-class'] || '') + ' ' + getClass(col);
  return bodyClass.trim() || '';
}

function getBodyStyle(col) {
  if (col.props['body-style']) {
    return (
      (typeof col.props['body-style'] === 'object'
        ? styleObjectToString(col.props['body-style'])
        : col.props['body-style']) +
      ' ' +
      getStyle(col)
    );
  }
  return '';
}

function getFooterStyle(col) {
  const rawFooterStyle = col.props['footer-style'];
  const normalizedFooterStyle =
    typeof rawFooterStyle === 'object' ? styleObjectToString(rawFooterStyle) : rawFooterStyle || '';

  const normalizedStyle = getStyle(col); // already stringified

  return [normalizedFooterStyle, normalizedStyle].filter(Boolean).join('; ').trim();
}
function getFooterClass(col) {
  const footerClass = (col.props['footer-class'] || '') + ' ' + getClass(col);
  return footerClass.trim() || '';
}

function doesAllOfThemHaveFooter() {
  if (!columns.value) {
    return false;
  }
  const allHaveFooter = columns.value.some((col) => {
    if (!col.props) {
      return false;
    }

    if (!col.children) {
      return false;
    }
    const footer = col.children['footer'];
    return footer !== undefined;
  });
  return allHaveFooter;
}
</script>
<template>
  <table class="border-collapse border border-gray-200">
    <thead class="sticky top-0 z-20">
      <tr>
        <th
          v-for="(col, index) in columns"
          :key="generateRandomKey(index)"
          :style="getHeaderStyle(col)"
          :class="getHeaderClass(col)"
          class="border border-gray-200"
        >
          <component :is="(col.children as any)?.header" />
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(child, dataIndex) in data" :key="generateRandomKey(dataIndex)">
        <th
          v-for="(col, index) in columns"
          :key="generateRandomKey(index)"
          :style="getBodyStyle(col)"
          :class="getBodyClass(col)"
          style="padding: 0"
          class="border border-gray-200"
        >
          <component :is="(col.children as any)?.body" :value="child[col.props?.field]" :data="child" />
        </th>
      </tr>
    </tbody>
    <tfoot class="sticky bottom-0" style="z-index: 20">
      <tr v-if="doesAllOfThemHaveFooter()">
        <th
          v-for="(col, index) in columns"
          :key="generateRandomKey(index)"
          class="border border-gray-200"
          :style="getFooterStyle(col)"
          :class="getFooterClass(col)"
        >
          <component :is="(col.children as any)?.footer" />
        </th>
      </tr>
    </tfoot>
  </table>
</template>
